import { createThemeCollection } from '@pierre/theming'
import { normalizeTheme, type ThemeRegistrationAny } from 'shiki/core'

type ThemeDescriptorInput = {
  name: string
  load: () => Promise<ThemeRegistrationAny | { default: ThemeRegistrationAny }>
  colorScheme?: 'light' | 'dark'
  collection?: string
  displayName?: string
}

const unwrapDefault = <T>(value: T | { default: T }): T =>
  value !== null && typeof value === 'object' && 'default' in value
    ? value.default
    : value

/**
 * The theme half of `@pierre/theming`, without the themes.
 *
 * The real entry point is the one place in the package that pulls in every
 * bundled theme — its own ten and Shiki's sixty-odd — each as its own lazily
 * loaded chunk. `@pierre/diffs` walks that collection on startup to register a
 * loader per theme, so the bundler publishes all of them.
 *
 * This portal shows F-plus in the two F-plus themes and nothing else, and
 * `registerFPlusDiffHighlighting` hands both to the renderer directly. So the
 * collections are empty and a theme that was not registered that way now fails
 * to resolve instead of being fetched.
 *
 * `createTheme` is the package's own, kept faithful to it: a descriptor whose
 * loader normalises what it resolves to.
 */
export const createTheme = ({
  name,
  load,
  colorScheme,
  collection,
  displayName,
}: ThemeDescriptorInput) => ({
  name,
  colorScheme,
  collection,
  displayName,
  load: async () => normalizeTheme(unwrapDefault(await load())),
})

export const pierreThemes = createThemeCollection({ themes: [] })
export const shikiThemes = createThemeCollection({ themes: [] })
export const themes = createThemeCollection({ themes: [] })
